// ---------------------------------------------------------------------------
// useEvaluationTemplateCatalogs — fetches the two catalogs the form needs:
//   • Graders — populates each GradersList dropdown.
//   • Agents — populates the agent picker.
// Both are loaded in every mode because the binding-flow panel analyzes the
// selected resources even when the surrounding controls are read-only.
//
// Both fetches use the same cancellation-flag pattern as the rest of the
// app's hooks: a local `cancelled` flag short-circuits the .then/.catch/
// .finally if the effect re-fires before the request resolves.
// ---------------------------------------------------------------------------

import { useEffect, useMemo, useState } from 'react';

import type { IHttpClient } from '../../../../api/client.js';
import { useHttpClient } from '../../../../api/HttpClientContext.js';
import type { Agent } from '../../../../types/agent.js';
import type { CollectionResponse } from '../../../../types/common.js';
import type {
  AgentSummary,
  GraderSummary,
} from '../EvaluationTemplateForm.types.js';

export interface UseEvaluationTemplateCatalogsParams {
  /** Retained for call-site compatibility; catalogs are required in every mode. */
  viewOnly: boolean;
  /** Workspace context for the endpoints. Fetches wait until present. */
  activeWorkspaceName: string | null | undefined;
  /** Exact component versions referenced by a saved template, keyed by name. */
  pinnedAgentVersions?: Record<string, number>;
  pinnedGraderVersions?: Record<string, number>;
  /** Resources currently referenced by the form and requiring snapshot resolution. */
  selectedAgentNames?: readonly string[];
  selectedGraderNames?: readonly string[];
}

export interface UseEvaluationTemplateCatalogsResult {
  availableGraders: GraderSummary[];
  gradersLoading: boolean;
  /** De-duplicated by name — what the dropdowns actually render. */
  uniqueGraders: GraderSummary[];
  /** Existing agents in the workspace — populates the agent picker. */
  agents: AgentSummary[];
  agentsLoading: boolean;
  /** Catalog or snapshot resolution failure; selected resources are unsafe to analyze. */
  catalogError?: string | null;
  agentsError?: string | null;
  gradersError?: string | null;
}

function latestByVersion<T extends { version?: number }>(
  versions: T[],
): T | undefined {
  return versions.reduce<T | undefined>(
    (latest, candidate) =>
      latest === undefined || (candidate.version ?? 0) > (latest.version ?? 0)
        ? candidate
        : latest,
    undefined,
  );
}

interface CatalogResource {
  name: string;
  version?: number;
  spec?: unknown;
  variables?: unknown;
}

interface CatalogResolution<T> {
  resources: T[];
  error: string | null;
}

interface ResolveSelectedResourcesParams<T extends CatalogResource> {
  resources: T[];
  selectedNames?: readonly string[];
  pinnedVersions?: Record<string, number>;
  fetchPinned: (name: string, version: number) => Promise<T>;
  fetchVersions: (name: string) => Promise<T[]>;
  resourceLabel: string;
}

async function resolveSelectedResources<T extends CatalogResource>({
  resources,
  selectedNames,
  pinnedVersions,
  fetchPinned,
  fetchVersions,
  resourceLabel,
}: ResolveSelectedResourcesParams<T>): Promise<CatalogResolution<T>> {
  const results = await Promise.all(
    resources.map(async (resource) => {
      const pinnedVersion = pinnedVersions?.[resource.name];
      const selected = selectedNames?.includes(resource.name) ?? false;
      if (pinnedVersion === undefined && !selected) {
        return { resource };
      }

      try {
        if (pinnedVersion !== undefined) {
          const pinned = await fetchPinned(resource.name, pinnedVersion);
          return {
            resource: { ...resource, ...pinned, version: pinnedVersion },
          };
        }

        const latest = latestByVersion(await fetchVersions(resource.name));
        if (latest?.version === undefined) {
          return {
            resource,
            error: `${resourceLabel} ${resource.name} has no snapshot. Snapshot it before using it in an evaluation.`,
          };
        }
        return {
          resource: { ...resource, ...latest, version: latest.version },
        };
      } catch (error: unknown) {
        return {
          resource,
          error:
            error instanceof Error
              ? error.message
              : `Failed to resolve ${resourceLabel} ${resource.name}.`,
        };
      }
    }),
  );

  return {
    resources: results.map((result) => result.resource),
    error: results.find((result) => result.error !== undefined)?.error ?? null,
  };
}

function graderPath(workspaceName: string, name: string): string {
  return `workspaces/${encodeURIComponent(workspaceName)}/evaluation/graders/${encodeURIComponent(name)}`;
}

function agentPath(workspaceName: string, name: string): string {
  return `workspaces/${encodeURIComponent(workspaceName)}/evaluation/agents/${encodeURIComponent(name)}`;
}

async function fetchGraderResolution(
  httpClient: IHttpClient,
  workspaceName: string,
  resources: GraderSummary[],
  selectedNames?: readonly string[],
  pinnedVersions?: Record<string, number>,
): Promise<CatalogResolution<GraderSummary>> {
  return resolveSelectedResources({
    resources,
    selectedNames,
    pinnedVersions,
    resourceLabel: 'Grader',
    fetchPinned: async (name, version) => {
      const { data } = await httpClient.get<GraderSummary>(
        `${graderPath(workspaceName, name)}/versions/${String(version)}`,
      );
      return data;
    },
    fetchVersions: async (name) => {
      const { data } = await httpClient.get<CollectionResponse<GraderSummary>>(
        `${graderPath(workspaceName, name)}/versions`,
      );
      return data.value;
    },
  });
}

async function fetchAgentResolution(
  httpClient: IHttpClient,
  workspaceName: string,
  resources: AgentSummary[],
  selectedNames?: readonly string[],
  pinnedVersions?: Record<string, number>,
): Promise<CatalogResolution<AgentSummary>> {
  return resolveSelectedResources({
    resources,
    selectedNames,
    pinnedVersions,
    resourceLabel: 'Agent',
    fetchPinned: async (name, version) => {
      const { data } = await httpClient.get<AgentSummary>(
        `${agentPath(workspaceName, name)}/versions/${String(version)}`,
      );
      return data;
    },
    fetchVersions: async (name) => {
      const { data } = await httpClient.get<CollectionResponse<AgentSummary>>(
        `${agentPath(workspaceName, name)}/versions`,
      );
      return data.value;
    },
  });
}

export function useEvaluationTemplateCatalogs({
  viewOnly: _viewOnly,
  activeWorkspaceName,
  pinnedAgentVersions,
  pinnedGraderVersions,
  selectedAgentNames,
  selectedGraderNames,
}: UseEvaluationTemplateCatalogsParams): UseEvaluationTemplateCatalogsResult {
  const httpClient = useHttpClient();

  const [availableGraders, setAvailableGraders] = useState<GraderSummary[]>([]);
  const [gradersLoading, setGradersLoading] = useState(false);
  const [agents, setAgents] = useState<AgentSummary[]>([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [gradersError, setGradersError] = useState<string | null>(null);
  const [agentsError, setAgentsError] = useState<string | null>(null);

  useEffect(() => {
    if (activeWorkspaceName === null || activeWorkspaceName === undefined) {
      return;
    }
    let cancelled = false;
    setGradersLoading(true);
    setGradersError(null);
    httpClient
      .get<CollectionResponse<GraderSummary>>(
        `workspaces/${encodeURIComponent(activeWorkspaceName)}/evaluation/graders`,
      )
      .then(({ data }) =>
        fetchGraderResolution(
          httpClient,
          activeWorkspaceName,
          data.value,
          selectedGraderNames,
          pinnedGraderVersions,
        ),
      )
      .then((resolution) => {
        if (!cancelled) {
          setAvailableGraders(resolution.resources);
          setGradersError(resolution.error);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setAvailableGraders([]);
          setGradersError(
            error instanceof Error
              ? error.message
              : 'Failed to resolve Grader snapshots.',
          );
        }
      })
      .finally(() => {
        if (!cancelled) setGradersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    activeWorkspaceName,
    httpClient,
    pinnedGraderVersions,
    selectedGraderNames,
  ]);

  useEffect(() => {
    if (activeWorkspaceName === null || activeWorkspaceName === undefined) {
      return;
    }
    let cancelled = false;
    setAgentsLoading(true);
    setAgentsError(null);
    httpClient
      .get<CollectionResponse<Agent>>(
        `workspaces/${encodeURIComponent(activeWorkspaceName)}/evaluation/agents`,
      )
      .then(({ data }) =>
        fetchAgentResolution(
          httpClient,
          activeWorkspaceName,
          data.value.map(
            (agent): AgentSummary => ({
              name: agent.name,
              displayName: agent.displayName,
              spec: agent.spec,
              variables: agent.variables,
              isDraft: agent.isDraft,
            }),
          ),
          selectedAgentNames,
          pinnedAgentVersions,
        ),
      )
      .then((resolution) => {
        if (!cancelled) {
          setAgents(resolution.resources);
          setAgentsError(resolution.error);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setAgents([]);
          setAgentsError(
            error instanceof Error
              ? error.message
              : 'Failed to resolve Agent snapshots.',
          );
        }
      })
      .finally(() => {
        if (!cancelled) setAgentsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    activeWorkspaceName,
    httpClient,
    pinnedAgentVersions,
    selectedAgentNames,
  ]);

  const uniqueGraders = useMemo(() => {
    const seen = new Map<string, GraderSummary>();
    for (const g of availableGraders) {
      if (!seen.has(g.name)) seen.set(g.name, g);
    }
    return Array.from(seen.values());
  }, [availableGraders]);

  return {
    availableGraders,
    gradersLoading,
    uniqueGraders,
    agents,
    agentsLoading,
    catalogError: agentsError ?? gradersError,
    agentsError,
    gradersError,
  };
}
